import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/courses", label: "Courses" },
  { to: "/about", label: "About Us" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#4A5D52]/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-3">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_mhfa-course-hub/artifacts/1ae3h8nj_final%20logo%3A%20not%20right%20colour.png"
            alt="Rise Learning"
            className="h-32 md:h-36 w-auto object-contain scale-[1.6] group-hover:scale-[1.65] transition-transform duration-500 -ml-6 md:-ml-8 translate-y-3"
            style={{
              transformOrigin: "left center",
              mixBlendMode: "multiply",
              filter: "saturate(0.85) brightness(0.85) contrast(1.05)",
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[0.92rem] tracking-wide transition-colors ${
                  isActive ? "text-[#1E2A25]" : "text-[#4A5D52] hover:text-[#1E2A25]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-[#D88C76] transition-all duration-300 ${
                      isActive ? "w-4" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/#upcoming-courses" data-testid="nav-cta-enquire" className="btn-primary">
            Book an open course
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[#1E2A25]/15"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#4A5D52]/10"
        >
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-serif-display text-2xl text-[#1E2A25]"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/#upcoming-courses" data-testid="mobile-nav-cta" className="btn-primary w-fit mt-2">
              Book an open course
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
